import PostHog from "@/services/analytics/postHog";
import FacebookPixel from "@/services/analytics/facebookPixel";
import GoogleAds from "@/services/analytics/googleAds";
import Gtm from "@/services/analytics/gtm";
import Zbozi from "@/services/analytics/zbozi";
import { getCurrencyById } from "@/services/currencies/currenciesApi";
import type { OrderId } from "@/services/marketplace/order/orderTypes";
import type { PriceNumeric } from "@/services/prices/priceTypes";

// Business events that get reported to multiple analytics tools at once (PostHog, Meta Pixel, GTM/Google Ads, ...) live here instead of
// in components, so components stay focused on UI logic. Adding another tool for an already tracked event then means changing one
// function here, not hunting down every call site.

export const trackRegistrationDone = (): void => {
  Gtm.trackEvent({ event: Gtm.Event.RegistrationDone });
  GoogleAds.trackConversion(GoogleAds.Conversion.RegistrationDone);
  PostHog.trackEvent(PostHog.EventName.Registration_Done);
  FacebookPixel.trackEvent(FacebookPixel.Event.CompleteRegistration);
};

export const trackAddToCart = (): void => {
  PostHog.trackEvent(PostHog.EventName.Marketplace_ToCartBtn_User_Click);
  FacebookPixel.trackEvent(FacebookPixel.Event.AddToCart);
};

// Fired when the order is submitted, i.e. before the buyer is sent to the payment gateway — the payment may still fail.
export const trackPurchase = (value: number, currency: string): void => {
  FacebookPixel.trackEvent(FacebookPixel.Event.NewOrder, { value, currency });
};

// Fired once the payment gateway confirms the order is paid, so unlike trackPurchase() this reports money actually received.
export const trackPaymentSuccess = async (orderId: OrderId, totalPrice: PriceNumeric): Promise<void> => {
  PostHog.trackEvent(PostHog.EventName.Marketplace_Order_Completed, { orderId });
  Zbozi.trackConversion(orderId);

  // The Google Ads conversion needs the currency code, which only the currency list holds (the order carries an id).
  const currency = await getCurrencyById(totalPrice.currencyId).catch(() => undefined);
  if (currency === undefined) {
    console.warn("Currency not resolved — Google Ads purchase conversion not sent.");
    return;
  }
  GoogleAds.trackConversion(GoogleAds.Conversion.Purchase, {
    value: totalPrice.price,
    currency: currency.code,
    transaction_id: String(orderId),
  });
};
